<template>
  <div class="modal fade" id="taxBracketManagementModal" tabindex="-1" aria-labelledby="taxBracketManagementModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="taxBracketManagementModalLabel">Tax Bracket Management</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading tax brackets...</p>
          </div>

          <div v-else>
            <!-- Controls section -->
            <div class="d-flex justify-content-between mb-3">
              <h6 class="mb-0 mt-2">Manage Tax Brackets</h6>
              <button @click="showAddModal" class="btn btn-sm btn-primary">
                <i class="mdi mdi-plus me-1"></i> Add Tax Bracket
              </button>
            </div>

            <!-- Tax Brackets Display Section -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0">Current Tax Brackets</h6>
              </div>
              <div class="card-body">
                <div class="tax-brackets-visualization mb-4">
                  <div v-for="(bracket, index) in taxBrackets" :key="bracket.id" class="tax-bracket-bar">
                    <div class="bracket-range">
                      {{ formatCurrency(bracket.min_income) }} 
                      <span v-if="bracket.max_income"> - {{ formatCurrency(bracket.max_income) }}</span>
                      <span v-else>and above</span>
                    </div>
                    <div class="progress">
                      <div 
                        class="progress-bar" 
                        :class="getBracketColorClass(index)"
                        role="progressbar" 
                        :style="{ width: calculateBracketWidth(bracket, index) + '%' }" 
                        :aria-valuenow="bracket.tax_rate" 
                        aria-valuemin="0" 
                        aria-valuemax="100">
                        {{ bracket.tax_rate }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tax brackets table -->
            <div class="table-responsive">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Min Income</th>
                  <th>Max Income</th>
                  <th>Tax Rate (%)</th>
                  <th>Additional Amount</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                  <th class="text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="taxBrackets.length === 0">
                  <td colspan="7" class="text-center py-3">No tax brackets found</td>
                </tr>
                <tr v-for="bracket in taxBrackets" :key="bracket.id">
                  <td>{{ formatCurrency(bracket.min_income) }}</td>
                  <td>{{ bracket.max_income ? formatCurrency(bracket.max_income) : 'No Limit' }}</td>
                  <td>{{ bracket.tax_rate }}%</td>
                  <td>{{ formatCurrency(bracket.additional_amount) }}</td>
                  <td>{{ formatDate(bracket.effective_date) }}</td>
                  <td>{{ bracket.end_date ? formatDate(bracket.end_date) : 'No End Date' }}</td>
                  <td class="text-center">
                    <button @click="editTaxBracket(bracket)" class="btn btn-sm btn-info me-1">
                      <i class="mdi mdi-pencil"></i>
                    </button>
                    <button @click="deleteTaxBracket(bracket.id)" class="btn btn-sm btn-danger">
                      <i class="mdi mdi-trash-can"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit Tax Bracket Modal -->
  <div class="modal fade" id="taxBracketFormModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Edit Tax Bracket' : 'Add Tax Bracket' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveTaxBracket">
            <div class="mb-3">
              <label for="minIncome" class="form-label">Minimum Income <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="minIncome"
                    v-model="bracketForm.min_income"
                    required
                    min="0"
                    step="0.01"
                    @change="validateIncomeRange"
                >
              </div>
            </div>

            <div class="mb-3">
              <label for="maxIncome" class="form-label">Maximum Income</label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="maxIncome"
                    v-model="bracketForm.max_income"
                    min="0"
                    step="0.01"
                    @change="validateIncomeRange"
                >
              </div>
              <small class="text-muted">Leave blank for the highest bracket</small>
              <div v-if="incomeRangeError" class="text-danger mt-1">{{ incomeRangeError }}</div>
            </div>

            <div class="mb-3">
              <label for="taxRate" class="form-label">Tax Rate (%) <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    id="taxRate"
                    v-model="bracketForm.tax_rate"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                >
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="mb-3">
              <label for="additionalAmount" class="form-label">Additional Amount</label>
              <div class="input-group">
                <span class="input-group-text">KES</span>
                <input
                    type="number"
                    class="form-control"
                    id="additionalAmount"
                    v-model="bracketForm.additional_amount"
                    min="0"
                    step="0.01"
                >
              </div>
              <small class="text-muted">Fixed amount to add to calculation (for progressive tax)</small>
            </div>

            <div class="mb-3">
              <label for="effectiveDate" class="form-label">Effective Date <span class="text-danger">*</span></label>
              <input
                  type="date"
                  class="form-control"
                  id="effectiveDate"
                  v-model="bracketForm.effective_date"
                  required
              >
            </div>

            <div class="mb-3">
              <label for="endDate" class="form-label">End Date</label>
              <input
                  type="date"
                  class="form-control"
                  id="endDate"
                  v-model="bracketForm.end_date"
              >
              <small class="text-muted">Leave blank if there's no end date</small>
            </div>

            <div class="mb-3">
              <label for="taxType" class="form-label">Tax Type</label>
              <select class="form-select" id="taxType" v-model="bracketForm.tax_type">
                <option value="Income Tax">Income Tax</option>
                <option value="VAT">VAT</option>
                <option value="Capital Gains">Capital Gains</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="saveTaxBracket"
              :disabled="!isFormValid || submitLoading"
          >
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";

export default {
  name: 'TaxBracketManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      taxBrackets: [],
      isEditing: false,
      currentId: null,
      incomeRangeError: '',
      bracketForm: {
        min_income: '',
        max_income: '',
        tax_rate: '',
        additional_amount: 0,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: '',
        tax_type: 'Income Tax'
      },
      modal: null,
      formModal: null
    };
  },
  computed: {
    isFormValid() {
      return this.bracketForm.min_income !== '' && 
             this.bracketForm.tax_rate !== '' && 
             this.bracketForm.effective_date && 
             !this.incomeRangeError;
    }
  },
  mounted() {
    this.initModals();
  },
  methods: {
    initModals() {
      this.$nextTick(() => {
        // Initialize the main modal
        this.modal = new bootstrap.Modal(document.getElementById('taxBracketManagementModal'));
        
        // Initialize form modal
        this.formModal = new bootstrap.Modal(document.getElementById('taxBracketFormModal'));
        
        // Listen for modal hidden events to reset form
        document.getElementById('taxBracketFormModal').addEventListener('hidden.bs.modal', this.resetForm);
      });
    },
    showModal() {
      if (this.modal) {
        this.loadTaxBrackets();
        this.modal.show();
      } else {
        this.initModals();
        setTimeout(() => {
          if (this.modal) {
            this.loadTaxBrackets();
            this.modal.show();
          }
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    showAddModal() {
      this.isEditing = false;
      this.currentId = null;
      this.resetForm();
      if (this.formModal) {
        this.formModal.show();
      }
    },
    showEditModal() {
      if (this.formModal) {
        this.formModal.show();
      }
    },
    hideFormModal() {
      if (this.formModal) {
        this.formModal.hide();
      }
    },
    resetForm() {
      this.bracketForm = {
        min_income: '',
        max_income: '',
        tax_rate: '',
        additional_amount: 0,
        effective_date: new Date().toISOString().split('T')[0],
        end_date: '',
        tax_type: 'Income Tax'
      };
      this.incomeRangeError = '';
      this.submitLoading = false;
    },
    validateIncomeRange() {
      if (this.bracketForm.min_income !== '' && this.bracketForm.max_income !== '' && 
          parseFloat(this.bracketForm.min_income) >= parseFloat(this.bracketForm.max_income)) {
        this.incomeRangeError = 'Maximum income must be greater than minimum income';
        return false;
      } else {
        this.incomeRangeError = '';
        return true;
      }
    },
    loadTaxBrackets() {
      this.loading = true;
      
      const data = new FormData();
      data.append("function", "getTaxBrackets");
      
      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            
            if (res.data.success) {
              this.taxBrackets = res.data.data.taxBrackets || [];
              // Sort tax brackets by min_income to ensure visualization is correct
              this.taxBrackets.sort((a, b) => parseFloat(a.min_income) - parseFloat(b.min_income));
            } else {
              console.error("Error fetching tax brackets:", res.data.message);
              alert(res.data.message || "Failed to load tax brackets");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while loading tax brackets");
          });
    },
    editTaxBracket(bracket) {
      this.isEditing = true;
      this.currentId = bracket.id;
      
      // Copy bracket data to form
      this.bracketForm = {
        min_income: bracket.min_income,
        max_income: bracket.max_income || '',
        tax_rate: bracket.tax_rate,
        additional_amount: bracket.additional_amount || 0,
        effective_date: bracket.effective_date,
        end_date: bracket.end_date || '',
        tax_type: bracket.tax_type || 'Income Tax'
      };
      
      this.showEditModal();
    },
    deleteTaxBracket(id) {
      if (!confirm("Are you sure you want to delete this tax bracket? This may affect payroll calculations.")) {
        return;
      }
      
      this.loading = true;
      
      const data = new FormData();
      data.append("function", "deleteTaxBracket");
      data.append("id", id);
      
      execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            
            if (res.data.success) {
              alert(res.data.message || "Tax bracket deleted successfully");
              this.loadTaxBrackets();
            } else {
              alert(res.data.message || "Failed to delete tax bracket");
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            alert("An error occurred while deleting tax bracket");
          });
    },
    saveTaxBracket() {
      if (!this.isFormValid) {
        alert("Please fill all required fields and fix any validation errors");
        return;
      }
      
      this.submitLoading = true;
      
      const data = new FormData();
      data.append("function", this.isEditing ? "updateTaxBracket" : "createTaxBracket");
      
      if (this.isEditing) {
        data.append("id", this.currentId);
      }
      
      data.append("min_income", this.bracketForm.min_income);
      data.append("tax_rate", this.bracketForm.tax_rate);
      data.append("effective_date", this.bracketForm.effective_date);
      
      if (this.bracketForm.max_income) {
        data.append("max_income", this.bracketForm.max_income);
      }
      
      if (this.bracketForm.additional_amount) {
        data.append("additional_amount", this.bracketForm.additional_amount);
      }
      
      if (this.bracketForm.end_date) {
        data.append("end_date", this.bracketForm.end_date);
      }
      
      data.append("tax_type", this.bracketForm.tax_type);
      
      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;
            
            if (res.data.success) {
              alert(res.data.message || (this.isEditing ? "Tax bracket updated successfully" : "Tax bracket created successfully"));
              this.hideFormModal();
              this.loadTaxBrackets();
            } else {
              alert(res.data.message || "Failed to save tax bracket");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API Error:", error);
            alert("An error occurred while saving tax bracket");
          });
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
    getBracketColorClass(index) {
      const colorClasses = [
        'bg-success',
        'bg-info',
        'bg-primary',
        'bg-warning',
        'bg-danger'
      ];
      return colorClasses[index % colorClasses.length];
    },
    calculateBracketWidth(bracket, index) {
      if (this.taxBrackets.length <= 1) return 100;
      
      // For the last bracket (highest), set a fixed percentage
      if (index === this.taxBrackets.length - 1 || !bracket.max_income) {
        return 25;
      }
      
      const min = parseFloat(bracket.min_income);
      const max = parseFloat(bracket.max_income);
      const range = max - min;
      
      // Find the largest range to calculate percentages
      let largestRange = 0;
      this.taxBrackets.forEach((b, i) => {
        if (b.max_income && i < this.taxBrackets.length - 1) {
          const r = parseFloat(b.max_income) - parseFloat(b.min_income);
          largestRange = Math.max(largestRange, r);
        }
      });
      
      // Calculate width as percentage of largest range, with a minimum of 10%
      return Math.max(10, (range / largestRange) * 100);
    }
  }
};
</script>

<style scoped>
.tax-brackets-visualization {
  padding: 20px 0;
}

.tax-bracket-bar {
  margin-bottom: 15px;
}

.bracket-range {
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.progress {
  height: 30px;
  font-weight: bold;
  color: white;
}

.form-check {
  margin-bottom: 0.5rem;
}

.alert {
  margin-top: 1rem;
}
</style>